<template>
  <div class="redaction-prompt-lab">
    <v-alert v-if="!topic || !hasSources" type="info" dense text class="mb-0">
      Envoie d'abord un résultat depuis la Section 1 (bouton « Envoyer vers
      rédaction ») pour alimenter la chaîne de rédaction.
    </v-alert>

    <template v-else>
      <div class="text-body-2 mb-3">
        Sujet : <strong>{{ topic }}</strong> — {{ sources.length }} source(s)
        <span v-if="siteId">· site #{{ siteId }}</span>
      </div>

      <v-row dense align="center">
        <v-col cols="12" md="6">
          <v-select
            v-model="templateId"
            :items="templateItems"
            label="Template de rédaction"
            outlined
            dense
            hide-details
            :loading="loadingTemplates"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-btn
            color="primary"
            block
            :loading="running"
            :disabled="!canRun"
            @click="runChain"
          >
            Lancer la chaîne de rédaction
          </v-btn>
        </v-col>
      </v-row>

      <v-card
        v-for="stage in redacPrompts"
        :key="stage.step"
        outlined
        class="mt-4 pa-4"
      >
        <div class="d-flex align-center mb-2">
          <div class="text-subtitle-2">
            {{ stage.label }} ({{ stage.step }})
          </div>
          <v-spacer />
          <v-btn
            small
            color="success"
            outlined
            :loading="savingStep === stage.step"
            :disabled="!stage.binding_id"
            @click="saveStep(stage)"
          >
            Sauvegarder ce stage
          </v-btn>
        </div>

        <v-alert
          v-if="!stage.binding_id"
          type="warning"
          dense
          text
          class="mb-2"
        >
          Aucun prompt résolu pour ce step (site / template).
        </v-alert>

        <template v-if="redacTexts[stage.step]">
          <v-textarea
            v-model="redacTexts[stage.step].system"
            label="Prompt SYSTEM (cœur du prompt)"
            outlined
            auto-grow
            rows="8"
            :disabled="!stage.binding_id"
            hide-details
            class="mb-3"
          />
          <v-textarea
            v-model="redacTexts[stage.step].user"
            label="Prompt USER"
            outlined
            auto-grow
            rows="5"
            :disabled="!stage.binding_id"
            hide-details
          />
        </template>

        <template v-if="stageOutput(stage.step)">
          <div class="text-caption grey--text mt-3">
            {{ stageOutput(stage.step).model_id }}
            <span v-if="stageOutput(stage.step).error" class="error--text">
              — erreur : {{ stageOutput(stage.step).error }}
            </span>
          </div>
          <v-expansion-panels flat class="mt-1">
            <v-expansion-panel v-if="stageOutput(stage.step).available">
              <v-expansion-panel-header>
                Catégories / tags disponibles
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <pre class="prompt-rendered">{{
                  formatOutput(stageOutput(stage.step).available)
                }}</pre>
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel>
              <v-expansion-panel-header
                >Sortie du stage</v-expansion-panel-header
              >
              <v-expansion-panel-content>
                <pre class="prompt-rendered">{{
                  formatOutput(stageOutput(stage.step).output)
                }}</pre>
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel>
              <v-expansion-panel-header>
                Prompts rendus (debug)
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div class="text-caption grey--text">SYSTEM :</div>
                <pre class="prompt-rendered mb-2">{{
                  stageOutput(stage.step).rendered_system
                }}</pre>
                <div class="text-caption grey--text">USER :</div>
                <pre class="prompt-rendered">{{
                  stageOutput(stage.step).rendered_prompt
                }}</pre>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </template>
      </v-card>
    </template>
  </div>
</template>

<script>
import axios from "@axios";

export default {
  name: "RedactionPromptLab",

  props: {
    topic: { type: String, default: "" },
    sources: { type: Array, default: () => [] },
    siteId: { type: Number, default: null },
  },

  data() {
    return {
      templates: [],
      templateId: null,
      redacPrompts: [],
      redacTexts: {},
      redacPreview: null,
      loadingTemplates: false,
      running: false,
      savingStep: null,
    };
  },

  computed: {
    hasSources() {
      return (this.sources || []).length > 0;
    },

    templateItems() {
      const items = (this.templates || []).map((t) => ({
        text: t.name || `Template #${t.id}`,
        value: t.id,
      }));
      return [{ text: "Défaut (site)", value: null }, ...items];
    },

    canRun() {
      return (
        !!this.topic &&
        this.hasSources &&
        this.redacPrompts.some((p) => p.binding_id)
      );
    },
  },

  watch: {
    siteId() {
      this.reload();
    },
    templateId() {
      this.fetchRedacPrompts();
    },
  },

  mounted() {
    this.reload();
  },

  methods: {
    reload() {
      this.fetchTemplates();
      this.fetchRedacPrompts();
    },

    async fetchTemplates() {
      this.loadingTemplates = true;
      try {
        const { data } = await axios.get(
          "/api/ai-factory/sandbox/redac-templates",
          { params: { site_id: this.siteId } }
        );
        this.templates = data.templates || [];
      } catch (error) {
        this.$toast?.error(
          "Erreur chargement des templates: " + error.message
        ) || console.error(error);
      } finally {
        this.loadingTemplates = false;
      }
    },

    async fetchRedacPrompts() {
      this.redacPrompts = [];
      this.redacTexts = {};
      this.redacPreview = null;
      try {
        const { data } = await axios.get(
          "/api/ai-factory/sandbox/redac-prompts",
          { params: { site_id: this.siteId, template_id: this.templateId } }
        );
        this.redacPrompts = data.items || [];
        const texts = {};
        this.redacPrompts.forEach((p) => {
          texts[p.step] = {
            system: p.system_prompt || "",
            user: p.user_prompt || "",
          };
        });
        this.redacTexts = texts;
      } catch (error) {
        this.$toast?.error(
          "Erreur chargement prompts rédaction: " + error.message
        ) || console.error(error);
      }
    },

    async runChain() {
      this.running = true;
      this.redacPreview = null;
      try {
        const overrides = {};
        this.redacPrompts.forEach((p) => {
          const t = this.redacTexts[p.step] || {};
          overrides[p.step] = { system: t.system, user: t.user };
        });
        const { data } = await axios.post(
          "/api/ai-factory/sandbox/redaction/preview",
          {
            topic: this.topic,
            sources: this.sources,
            site_id: this.siteId,
            aif_custom_prompt_template_id: this.templateId,
            ...overrides,
          }
        );
        this.redacPreview = data;
      } catch (error) {
        const detail = error?.response?.data?.detail;
        this.$toast?.error("Erreur Lancer: " + (detail || error.message)) ||
          console.error(error);
      } finally {
        this.running = false;
      }
    },

    async saveStep(stage) {
      const ok = window.confirm(
        `Sauvegarder le prompt ${stage.step} ? Il deviendra la version active utilisée en production.`
      );
      if (!ok) return;
      this.savingStep = stage.step;
      try {
        const t = this.redacTexts[stage.step] || {};
        await axios.patch(
          `/api/ai-factory/sandbox/redac-prompts/${stage.binding_id}`,
          { system_prompt: t.system, user_prompt: t.user }
        );
        this.$toast?.success(
          `Prompt ${stage.step} sauvegardé (nouvelle révision active)`
        ) || console.log("redac stage saved");
      } catch (error) {
        const detail = error?.response?.data?.detail;
        this.$toast?.error("Erreur sauvegarde: " + (detail || error.message)) ||
          console.error(error);
      } finally {
        this.savingStep = null;
      }
    },

    stageOutput(step) {
      if (!this.redacPreview || !this.redacPreview.stages) return null;
      return this.redacPreview.stages.find((s) => s.step === step) || null;
    },

    formatOutput(output) {
      try {
        return JSON.stringify(output, null, 2);
      } catch (e) {
        return String(output);
      }
    },
  },
};
</script>

<style scoped>
.prompt-rendered {
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 12px;
  margin: 0;
}
</style>
